import { getMessage } from '@web3/base-lang';
import { GetStaticProps, GetStaticPaths } from 'next';
import { ALL_LOCALES, appPath } from '@web3/base-utils';
import { useMemo, useState, useEffect } from 'react';
import {
  Banner,
  Deposit,
  ClaimCard,
  Explore,
  TermsConditions,
  BanModal,
  TaskItem,
  PageSkeleton,
} from '@/containers/activity';
import { useActivityStore } from '@/store/activity';
import { useParams } from '@web3/base-hooks';
import { ActivityTag } from '@/types';
import { isMobile, WalletType } from '@web3/base-utils';
import cls from 'classnames';
import { WalletTypeTip } from '@/components/WalletTypeTip';
import { SwitchWallet } from '@/components/dialogs/SwitchWallet';
import { BybitWalletEx } from '@/config/common';
import { useShare, usePageData } from '@/containers/activity/hooks';
import { useEligibility } from '@/containers/activity/hooks';

import styles from './index.module.scss';

export default function Activity() {
  const { activityTag: activityTagParam, code, state, originUrl } = useParams();
  const [showSwitchWallet, setShowSwitchWallet] = useState(false);
  const { eligibilityCount } = useEligibility();
  const { pageLoading } = usePageData();
  const store = useActivityStore();
  const activityTag = Number(activityTagParam) || ActivityTag.hunter;
  const [showBanModal, setShowBanModal] = useState(false);

  // 活动支持的钱包类型列表
  const walletTypeList = useMemo(
    () => store.activityDetail?.activityInfo?.walletTypeList || [],
    [store.activityDetail]
  );
  // 仅展示助记词钱包和云钱包
  const walletTypeListFiltered = useMemo(() => {
    const filtered = walletTypeList.filter((item) =>
      [WalletType.CLOUD, ...BybitWalletEx].includes(item)
    );
    return filtered.sort((a, b) => b - a);
  }, [walletTypeList]);

  const { handleButtonClick } = useShare();

  useEffect(() => {
    if (!code) return;
    if (!state) return;
    if (!originUrl) return;

    if (originUrl.includes('bybit') || originUrl.includes('bycloud')) {
      window.opener?.postMessage({ code, state }, originUrl);
    }
  }, [code, state, originUrl]);

  return (
    <div
      className={cls(
        `${styles['airdrop-detail-wrap']} bg-base text-white pb-10`
      )}
      style={{
        minHeight: 'calc(100vh - 424px)',
      }}
    >
      <img
        className={styles.bannerImg}
        src={
          isMobile
            ? `${appPath}/svg/activity/airdrop-detail-h5-bg.png`
            : `${appPath}/svg/activity/airdrop-detail-bg.png`
        }
        alt="banner"
      />
      {pageLoading && <PageSkeleton />}
      {!pageLoading && (
        <>
          <Banner
            data={store.activityDetail?.activityInfo}
            handleShare={handleButtonClick}
            pageLoading={pageLoading}
          />
          {/* 切换钱包提示 */}
          {walletTypeList.length > 0 && (
            <WalletTypeTip walletTypeList={walletTypeListFiltered} />
          )}
          {/* 领取 claim 提示 */}
          {!isMobile && !!eligibilityCount && (
            <ClaimCard
              chainId={store.activityDetail.activityInfo.chainId}
              eligibilityCount={eligibilityCount}
            />
          )}
          {/* 3、4最常规的任务 */}
          {+activityTag >= ActivityTag.hunter && (
            <>
              {store.activityDetail && (
                <>
                  {!isMobile && !eligibilityCount && (
                    <Deposit
                      onShowSwitchWallet={() => setShowSwitchWallet(true)}
                    />
                  )}
                  <Explore
                    handleShare={handleButtonClick}
                    onShowSwitchWallet={() => setShowSwitchWallet(true)}
                    onShowBanModal={() => {
                      setShowBanModal(true);
                    }}
                  />
                </>
              )}
              <TermsConditions data={store.activityDetail?.activityInfo} />
            </>
          )}
          {+activityTag < ActivityTag.hunter &&
            store.taskList?.map((task) => {
              return (
                <TaskItem
                  data={task}
                  handleShare={handleButtonClick}
                  key={task.configId}
                />
              );
            })}
          {/* 钱包类型不匹配提示弹窗 */}
          <SwitchWallet
            visible={showSwitchWallet}
            walletTypeList={walletTypeListFiltered}
            handleCancel={() => setShowSwitchWallet(false)}
          />
          <BanModal
            show={showBanModal}
            onClose={() => {
              setShowBanModal(false);
            }}
          />
        </>
      )}
    </div>
  );
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context.params as any;

  const messages = await getMessage(
    'web3-airdrop',
    {
      locale,
      entry: import.meta.url,
      additions: ['airdropSeoTitle', 'airdropKeyword', 'airdropSeoDesc'],
    },
    false
  );

  return {
    props: {
      locale,
      messages,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: ALL_LOCALES.map((locale) => ({
      params: {
        locale: locale.value,
      },
    })),
    fallback: true,
  };
};
